import React, {useEffect, useState, Suspense, startTransition} from 'react';
import {BrowserRouter, useLocation} from 'react-router-dom';
import {Toaster} from 'sonner';

import {loadStorefront, readCachedStorefront, writeCachedStorefront} from './lib/catalog';
import {StorefrontBridge} from './components/StorefrontBridge';
import {importWithRetry} from './lib/chunkRetry';
import {prefetchCatalogFor} from './components/useCatalog';
const AdminApp=React.lazy(()=>import('./admin/AdminApp'));
import './original/original.css';
import './App.css';
import './original/layout-fixes.css';
import './storefront-responsive.css';

let storefrontModule;
const loadStorefrontModule=()=>importWithRetry(()=>import('./original/Storefront'));
const STOREFRONT_ERROR_TEXT={uk:'Не вдалося завантажити сторінку. Перевірте з’єднання та спробуйте ще раз.',ru:'Не удалось загрузить страницу. Проверьте соединение и попробуйте ещё раз.'};
function StorefrontLoader() {
  const [Component,setComponent]=useState(null);
  const [error,setError]=useState('');
  const [,setVersion]=useState(0);
  const loadData=()=>{
    const cached=readCachedStorefront();
    window.__ASANS_DATA__=window.__ASANS_DATA__||cached||{products:[],categories:[],content:{},text_overrides:{}};
    storefrontModule=storefrontModule || loadStorefrontModule().catch(error=>{storefrontModule=null;throw error;});
    // Repeat visit: paint immediately from the last saved catalog, then refresh in place.
    let fresh=false, painted=false;
    if(cached){
      storefrontModule.then(module=>{
        if(fresh) return;
        module.updateCatalog(window.__ASANS_DATA__);
        painted=true;
        setComponent(()=>module.default);
      }).catch(()=>{});
    }
    // First visit: paint as soon as the first catalog page (categories + content + 100 products) arrives.
    const paintFirst=first=>{
      if(fresh||painted||cached) return;
      storefrontModule.then(module=>{
        if(fresh||painted) return;
        window.__ASANS_DATA__=first;
        module.updateCatalog(first);
        painted=true;
        setComponent(()=>module.default);
      }).catch(()=>{});
    };
    Promise.all([loadStorefront(undefined,paintFirst),storefrontModule]).then(([data,module])=>{
      // Validate data has required fields before using
      if(!data || !Array.isArray(data.products) || !Array.isArray(data.categories)){
        throw new Error('Invalid storefront data received from server');
      }
      fresh=true;
      window.__ASANS_DATA__=data;
      module.updateCatalog(data);
      // The full catalogue re-renders every visible list: do it as an interruptible transition so
      // scrolling and taps stay responsive, and save the multi-MB cache copy when the page is idle.
      startTransition(()=>{ setComponent(()=>module.default); setVersion(v=>v+1); });
      const save=()=>writeCachedStorefront(data);
      if(window.requestIdleCallback) window.requestIdleCallback(save,{timeout:5000}); else setTimeout(save,1500);
    }).catch(error=>{ if(!painted&&!document.querySelector('.pdp-title, header')) setError(error.message); });
  };
  useEffect(()=>{
    let mounted=true;
    prefetchCatalogFor(window.location.pathname,window.location.search);
    loadData();
    // Reload data when page gets focus (user returns from admin)
    const onFocus=()=>{if(mounted)loadData();};
    window.addEventListener('focus',onFocus);
    return()=>{mounted=false;window.removeEventListener('focus',onFocus);};
  },[]);
  if(error) return <div className="store-status" data-testid="storefront-error"><h1>ASANS</h1><p>{STOREFRONT_ERROR_TEXT[document.documentElement.lang==='ru'?'ru':'uk']}</p><button className="btn-solid" data-testid="retry-storefront" onClick={()=>window.location.reload()}>{document.documentElement.lang==='ru'?'Попробовать ещё раз':'Спробувати ще раз'}</button></div>;
  return Component ? <><Component/><StorefrontBridge/></> : <div className="store-loading" aria-label="ASANS" data-testid="storefront-loading"/>;
}
function AppRouter(){
  const location=useLocation();
  return location.pathname.startsWith('/admin') ? <AdminApp/> : <StorefrontLoader/>;
}
export default function App(){
  useEffect(()=>{if('serviceWorker' in navigator){navigator.serviceWorker.register('/sw.js').catch(()=>{});}},[]);
  return <BrowserRouter><Suspense fallback={null}><AppRouter/></Suspense><Toaster position="bottom-right" richColors/></BrowserRouter>;
}
